/**
 * RESPONSIBILITY: draw one event as a row in the All Events list.
 *
 * A row rather than a card because the list is scanned by date, and a picture the width
 * of the page pushes the next event off the screen.
 *
 * THE WHOLE ROW IS THE LINK, like the cards elsewhere. The title carries it and the
 * stylesheet stretches it over the row, so the picture and the words are the target too
 * — anchors cannot nest, and a row that looks like one thing should not have one
 * clickable corner. The button sits above it and keeps its own destination.
 */
import type { EventRow } from '@app/sections';
import { Picture } from '../commons/Picture';
import { eventHref } from './event-href';
import { whenText, isVisible } from '../commons/when';
import { NotPublic } from '../commons/NotPublic';
import { PlaceMark } from '../commons/PlaceMark';
import { EventTags } from './EventTags';

export interface EventRowItemProps {
  e: EventRow;
  /** The word on the button when this event gives it none of its own. */
  detailsLabel: string;
}

/** Marks itself, for the reason given on EventCard. */
export function EventRowItem({ e, detailsLabel }: EventRowItemProps) {
  const draft = !isVisible(e);

  return (
    <article className={draft ? 'evrow is-draft' : 'evrow'}>
      <Picture value={e.image} alt="" className="evrow-pic" />
      <div>
        <p className="meta">{whenText(e)}</p>
        <h3><a className="evrow-link" href={eventHref(e)}>{e.title}</a></h3>
        {draft && <NotPublic />}
        {e.summary && <p>{e.summary}</p>}
        {e.location && <p className="ev-where"><PlaceMark />{e.location}</p>}
        <EventTags e={e} />
      </div>
      {/* Its OWN words when it has them — "Register", "Sign up" — and the list's default
          otherwise. That is why one row says REGISTER and the rest say Details: it is
          content, set per event, not something the page decided. */}
      <a className={e.ctaLabel ? 'btn' : 'btn btn-ghost'} href={eventHref(e)}>
        {e.ctaLabel || detailsLabel}
      </a>
    </article>
  );
}
